import { Alert, Button, Empty, Modal, Space, Spin, Tooltip, Typography } from "antd";
import { DownloadOutlined, EyeOutlined, FileWordOutlined, FilePdfOutlined, SyncOutlined } from "@ant-design/icons";

import styles from "./DocumentBuilder.module.css";

const { Text } = Typography;

/**
 * Generate and download buttons, shown both beside the document structure and
 * in the preview pop-up. The preview button is only offered outside the pop-up.
 */
export const GenerateControls = ({ result, generating, canGenerate, onGenerate, onDownloadDocx, onDownloadPdf, onPreview }) => (
  <Space size={4}>
    <Tooltip title={canGenerate ? "" : "Add a section with content first"}>
      <Button type="primary" size="small" icon={<SyncOutlined />} loading={generating} disabled={!canGenerate} onClick={onGenerate}>
        {result ? "Regenerate" : "Generate"}
      </Button>
    </Tooltip>
    {onPreview && (
      <Tooltip title="Open the last generated preview">
        <Button size="small" icon={<EyeOutlined />} disabled={!result || generating} onClick={onPreview} />
      </Tooltip>
    )}
    <Tooltip title="Download Word document">
      <Button size="small" icon={<FileWordOutlined />} disabled={!result} onClick={onDownloadDocx} />
    </Tooltip>
    <Tooltip title="Download PDF">
      <Button size="small" icon={<FilePdfOutlined />} disabled={!result?.pdfUrl} onClick={onDownloadPdf} />
    </Tooltip>
  </Space>
);

/**
 * The generated document, rendered as PDF, in a pop-up over the builder.
 */
const PreviewModal = ({ open, onClose, result, generating, canGenerate, pdfAvailable, onGenerate, onDownloadDocx, onDownloadPdf }) => (
  <Modal
    open={open}
    onCancel={onClose}
    title={
      <div className={styles.previewTitle}>
        <span>Preview</span>
        <GenerateControls
          result={result}
          generating={generating}
          canGenerate={canGenerate}
          onGenerate={onGenerate}
          onDownloadDocx={onDownloadDocx}
          onDownloadPdf={onDownloadPdf}
        />
      </div>
    }
    footer={null}
    width="min(1200px, 94vw)"
    centered
    styles={{ body: { height: "82vh", display: "flex", flexDirection: "column", gap: 10 } }}
  >
    {!pdfAvailable && (
      <Alert
        type="warning"
        showIcon
        message="PDF rendering is unavailable on this server"
        description="The Word document can still be generated and downloaded."
      />
    )}

    {result?.warnings?.length > 0 && (
      <Alert
        type="warning"
        showIcon
        message={`${result.warnings.length} item${result.warnings.length === 1 ? "" : "s"} needed attention`}
        description={
          <ul className={styles.warningList}>
            {result.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        }
      />
    )}

    <div className={styles.previewFrame}>
      {generating && (
        <div className={styles.centered}>
          <Space direction="vertical" align="center" size={12}>
            <Spin />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Assembling the document...
            </Text>
          </Space>
        </div>
      )}

      {!generating && !result && (
        <div className={styles.centered}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <Text type="secondary" style={{ fontSize: 12 }}>
                Build a structure, then generate to see the document here
              </Text>
            }
          />
        </div>
      )}

      {!generating && result?.pdfUrl && (
        <iframe title="Document preview" src={`${result.pdfUrl}#view=FitH`} className={styles.previewDocument} />
      )}

      {!generating && result && !result.pdfUrl && (
        <div className={styles.centered}>
          <Space direction="vertical" align="center">
            <Text type="secondary" style={{ fontSize: 12 }}>
              {result.docxName} is ready
            </Text>
            <Button icon={<DownloadOutlined />} onClick={onDownloadDocx}>
              Download Word document
            </Button>
          </Space>
        </div>
      )}
    </div>
  </Modal>
);

export default PreviewModal;
